import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  type DraggableAttributes,
  type DraggableSyntheticListeners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import { arrayMove, horizontalListSortingStrategy, SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS as DndCss } from "@dnd-kit/utilities";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Box,
  Checkbox,
  Menu,
  MenuItem,
  type SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  type Theme,
} from "@mui/material";
import {
  type Cell,
  type Column,
  type ColumnDef,
  type ColumnSizingState,
  flexRender,
  type Header,
  type Row,
  type RowData,
  type Table as TanstackTable,
} from "@tanstack/react-table";
import { defaultRangeExtractor, type Range, useVirtualizer } from "@tanstack/react-virtual";
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import { clampAutoFitWidth, measureColumnContentWidth } from "./columnAutoFit";
import { resolveLeftPinned } from "./columnPinning";
import type { DataTableFeatures, DataTableInstance } from "./dataTableFeatures";
import { probeRow } from "./rowRenderProbe";
import { keepsBrowserMenu } from "./textEntry";

const DRAG_HANDLE_OFFSET = 20;

/** Extra px on table minWidth so horizontal scroll isn’t short of the true width (scrollbar/rounding). */
const TABLE_MIN_WIDTH_BUFFER_PX = 12;

// Deterministic pixel width for a column the layout must size exactly: a resized column or any pinned column. Returns
// the column's current size (TanStack's `getSize` already reflects a resize, else the def size) clamped to
// `[minSize, maxSize]`. Both the pinned sticky-offset accumulators and the `<colgroup>` size pinned columns through
// this, so a pinned column renders at exactly the width the offsets assume. A flex-sized pinned column would otherwise
// render wider than the accumulator expects and overlap its neighbor when scrolled, hiding the column divider.
function fixedColumnPixelWidth(col: {
  getSize: () => number;
  columnDef: { minSize?: number; maxSize?: number };
}): number {
  const raw = col.getSize();
  const minW = col.columnDef.minSize;
  const maxW = col.columnDef.maxSize;
  const clampedToMin = minW != null ? Math.max(raw, minW) : raw;
  return maxW != null ? Math.min(clampedToMin, maxW) : clampedToMin;
}

/**
 * The sticky `left` offset in px of each leading start-pinned column, by id: the summed widths of the pinned columns
 * before it. Stops at the first column that is not pinned, so it takes the columns in display order.
 */
export function leftPinnedOffsets<T extends RowData>(
  columns: readonly Column<DataTableFeatures, T>[],
): Record<string, number> {
  const offsets: Record<string, number> = {};
  let left = 0;
  for (const column of columns) {
    if (column.getIsPinned() !== "start") break;
    offsets[column.id] = left;
    left += fixedColumnPixelWidth(column);
  }
  return offsets;
}

/**
 * Selects every row between two rows (inclusive), preserving the existing selection. Shared by shift-click on the
 * checkbox column and the `X` keyboard shortcut so the two range-select paths can't drift apart.
 *
 * @param table - The table whose row selection is updated.
 * @param anchorRowId - Row id of the last selection anchor (where the range starts).
 * @param targetRowId - Row id of the row the range extends to.
 */
export function applyRangeSelection<T extends RowData>(
  table: TanstackTable<DataTableFeatures, T>,
  anchorRowId: string,
  targetRowId: string,
): void {
  const rows = table.getRowModel().rows;
  const anchorIndex = rows.findIndex((r) => r.id === anchorRowId);
  const targetIndex = rows.findIndex((r) => r.id === targetRowId);
  if (anchorIndex === -1 || targetIndex === -1) return;
  const start = Math.min(anchorIndex, targetIndex);
  const end = Math.max(anchorIndex, targetIndex);
  table.setRowSelection((prev) => {
    const next = { ...prev };
    for (let i = start; i <= end; i++) next[rows[i].id] = true;
    return next;
  });
}

export function buildCheckboxColumn<T extends RowData>(options?: {
  shiftSelectRef?: React.MutableRefObject<string | null>;
  onBulkSelect?: () => void;
}): ColumnDef<DataTableFeatures, T, unknown> {
  return {
    id: "_select",
    header: ({ table }) => (
      <Checkbox
        size="small"
        checked={table.getIsAllRowsSelected()}
        indeterminate={table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
        onChange={(e) => {
          table.getToggleAllRowsSelectedHandler()(e);
          options?.onBulkSelect?.();
        }}
      />
    ),
    cell: ({ row, table }) => (
      <Checkbox
        size="small"
        checked={row.getIsSelected()}
        indeterminate={row.getIsSomeSelected()}
        onClick={(e) => {
          e.stopPropagation();

          const ref = options?.shiftSelectRef;

          if (ref && e.shiftKey && ref.current !== null) {
            applyRangeSelection(table, ref.current, row.id);
            return;
          }

          row.toggleSelected();
          if (ref) {
            ref.current = row.id;
          }
        }}
      />
    ),
    meta: { flex: 0, compact: true, frozen: true, alwaysPinned: true },
    size: 36,
    enableSorting: false,
    enableGlobalFilter: false,
    enableResizing: false,
    enableHiding: false,
  };
}

/**
 * A group header interleaved with the rows. The rows it covers follow it in the table's data, `count` of them, unless
 * it is `collapsed`, in which case the caller leaves them out of the data and the header alone shows with the count.
 */
export type DataTableGroup = {
  key: string;
  label: string;
  count: number;
  collapsed: boolean;
  /** A mark drawn before the label, setting the group apart from the others. */
  icon?: React.ReactNode;
};

/**
 * The virtualizer's rendered indexes, plus the header of the group the top of the viewport is in when that header has
 * scrolled out of the window. The header sticks beneath the column header, so it has to stay mounted for as long as
 * its group's rows are on screen; the next group's header, later in the DOM, covers it as that group scrolls up.
 *
 * @param range - The virtualizer's visible range and overscan.
 * @param groupIndexes - The item index of every group header, ascending.
 */
export function withActiveGroupHeader(range: Range, groupIndexes: readonly number[]): number[] {
  const indexes = defaultRangeExtractor(range);
  const active = groupIndexes.findLast((index) => index <= range.startIndex);
  if (active == null || active >= (indexes[0] ?? 0)) return indexes;
  return [active, ...indexes];
}

/** What the body renders in order: a data row or a group header. */
type BodyItem<T extends RowData> =
  | { kind: "row"; row: Row<DataTableFeatures, T> }
  | { kind: "group"; group: DataTableGroup };

/** Interleaves group headers with the rows they cover; without groups every row is an item. */
function interleaveGroups<T extends RowData>(
  rows: Row<DataTableFeatures, T>[],
  groups: readonly DataTableGroup[] | undefined,
): BodyItem<T>[] {
  if (!groups || groups.length === 0) return rows.map((row) => ({ kind: "row", row }));
  const items: BodyItem<T>[] = [];
  let cursor = 0;
  for (const group of groups) {
    items.push({ kind: "group", group });
    if (group.collapsed) continue;
    for (const row of rows.slice(cursor, cursor + group.count)) items.push({ kind: "row", row });
    cursor += group.count;
  }
  return items;
}

type DataTableProps<T extends RowData> = {
  table: DataTableInstance<T>;
  onRowClick?: (row: T) => void;
  onRowContextMenu?: (row: T, event: React.MouseEvent) => void;
  onRowDoubleClick?: (row: T, event: React.MouseEvent) => void;
  /** A left click on a header cell (not its grabber or resize handle); the column and the cell element that was hit. */
  onHeaderClick?: (column: Column<DataTableFeatures, T>, event: React.MouseEvent<HTMLElement>) => void;
  getRowClassName?: (row: T) => string;
  /** When set, controls cursor per row; otherwise rows use `pointer` whenever `onRowClick` is defined. */
  getRowCursor?: (row: T) => string;
  noRowsContent?: React.ReactNode;
  pagination?: boolean;
  pageSizeOptions?: number[];
  toolbar?: React.ReactNode;
  rowHeight?: number;
  headerHeight?: number;
  virtualize?: boolean;
  enableColumnHiding?: boolean;
  onHideColumn?: (columnId: string) => void;
  /**
   *  When true, the table shrinks to fit its content instead of filling 100% of the parent height.
   *  The table is still capped at 100% of the parent height, and rows scroll when they overflow.
   */
  contentFit?: boolean;
  /**
   * Optional user override of the left-pinned columns. When omitted, pinning falls back to the columns' `meta.frozen`
   * default. The selection checkbox (`_select`) is always pinned first regardless.
   */
  columnPinning?: { left: string[] };
  /** Persists a new left-pinned set (the user-managed columns, excluding `alwaysPinned`). Enables header pin/unpin. */
  onColumnPinningChange?: (left: string[]) => void;
  /** Id of the row to keep scrolled into view (virtualized tables only), e.g. a roving keyboard highlight. */
  scrollToRowId?: string | null;
  /**
   * Id of the one row rendered with a detail row beneath it (see `renderExpandedRow`). Other rows keep their height,
   * so moving the expansion from one row to the next shifts nothing else on screen.
   */
  expandedRowId?: string | null;
  /** Renders the detail row shown under `expandedRowId`, spanning every visible column. */
  renderExpandedRow?: (row: T) => React.ReactNode;
  /**
   * The detail row's expected height in px, for a virtualized table: the estimate the expanded item is sized by until
   * the rendered detail has been measured (it is measured again whenever it resizes). Without it the first paint of a
   * detail lands the scroll height a detail's worth off until the measurement corrects it.
   */
  expandedRowHeight?: number;
  /** Browser-test id stamped on every body row as `data-sb` (stripped from production builds). */
  rowTestId?: string;
  /** Browser-test id stamped on the scroll container, so a journey can scroll the table and read where it sits. */
  containerTestId?: string;
  /**
   * Group headers to interleave with the rows (see {@link DataTableGroup}); the data must be ordered group by group.
   * Header rows are not rows: they carry no data, take no click but their own fold, and never expand.
   */
  groups?: readonly DataTableGroup[];
  /** Folds or unfolds a group when its header is clicked. */
  onToggleGroup?: (key: string) => void;
  /** Browser-test id stamped on every group header row. */
  groupTestId?: string;
  sx?: SxProps<Theme>;
};

/**
 * Publishes a measured px value as a CSS variable on the scroll container, and again whenever the measured node
 * resizes. It is written to the style directly, so a resize re-renders nothing.
 *
 * @param scrollRef - The scroll container the variable is set on.
 * @param enabled - Whether anything reads the variable; when false nothing is measured.
 * @param name - The CSS variable's name.
 * @param resolveNode - Finds the node to measure within the container, or null when it is not rendered.
 * @param measure - The value in px to publish for that node.
 * @param remountKey - Changes whenever the container remounts, so the new one is measured.
 */
function usePublishedCssVar(
  scrollRef: React.RefObject<HTMLDivElement | null>,
  enabled: boolean,
  name: string,
  resolveNode: (container: HTMLDivElement) => Element | null,
  measure: (node: Element) => number,
  remountKey: unknown,
): void {
  // biome-ignore lint/correctness/useExhaustiveDependencies: `remountKey` stands in for a remounted container; callbacks are inline
  useLayoutEffect(() => {
    const container = scrollRef.current;
    const node = container ? resolveNode(container) : null;
    if (!enabled || !container || !node) return;
    const publish = () => container.style.setProperty(name, `${measure(node)}px`);
    publish();
    // jsdom has no ResizeObserver; there the value on mount stands.
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(publish);
    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled, name, remountKey]);
}

/** The column header's rendered height, published as a CSS variable for group headers to stick beneath. */
const HEADER_HEIGHT_VAR = "--datatable-header-height";

/**
 * A group header spanning every visible column. It sticks beneath the column header while its group scrolls under it,
 * and its label sticks to the left edge under horizontal scroll.
 */
function GroupHeaderRow({
  group,
  colSpan,
  rowHeight,
  onToggle,
  testId,
  measureRef,
}: {
  group: DataTableGroup;
  colSpan: number;
  rowHeight: number;
  onToggle?: () => void;
  testId?: string;
  /** Lets a virtualized body measure a real header's height (once) and size every group item by it. */
  measureRef?: (node: HTMLTableRowElement | null) => void;
}) {
  return (
    <TableRow
      ref={measureRef}
      className="datatable-group-row"
      data-sb={testId}
      aria-expanded={!group.collapsed}
      onClick={onToggle}
      sx={{ height: rowHeight, cursor: onToggle ? "pointer" : "default" }}
    >
      {/* Above the body's pinned cells; it sticks beneath the column header, so never overlaps its cells. */}
      <TableCell
        colSpan={colSpan}
        sx={{
          py: 0,
          position: "sticky",
          top: `var(${HEADER_HEIGHT_VAR}, 0px)`,
          zIndex: 2,
          bgcolor: "background.default",
        }}
      >
        <Box
          sx={{ position: "sticky", left: 8, display: "inline-flex", alignItems: "center", gap: 0.75, fontWeight: 600 }}
        >
          {group.collapsed ? <ChevronRightIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
          {group.icon}
          <span>{group.label}</span>
          <Box component="span" sx={{ opacity: 0.6, fontWeight: 400, fontVariantNumeric: "tabular-nums" }}>
            {group.count}
          </Box>
        </Box>
      </TableCell>
    </TableRow>
  );
}

/** The scroll container's visible width, published as a CSS variable for a detail row's content to hold to. */
const VISIBLE_WIDTH_VAR = "--datatable-visible-width";

/**
 * The detail row under an expanded row. The cell spans every column, but its content holds to the scroll container's
 * visible width and sticks to its left edge, so the whole detail stays on screen however wide the table and however
 * far it is scrolled sideways.
 */
function ExpandedDetailRow({
  colSpan,
  children,
  measureRef,
}: {
  colSpan: number;
  children: React.ReactNode;
  /** Lets a virtualized body measure the detail's height and size the expanded item by it. */
  measureRef?: (node: HTMLTableRowElement | null) => void;
}) {
  return (
    <TableRow ref={measureRef} className="datatable-row-detail">
      {/* A clipping cell would be the sticky content's scroll container, so the cell lets it overflow. */}
      <TableCell colSpan={colSpan} sx={{ p: 0, overflow: "visible" }}>
        <Box sx={{ position: "sticky", left: 0, width: `var(${VISIBLE_WIDTH_VAR}, 100%)` }}>{children}</Box>
      </TableCell>
    </TableRow>
  );
}

/** What a body row takes: the row and its cells, where its pinned cells sit, and how its `<tr>` looks and behaves. */
export type BodyRowProps<T extends RowData> = {
  row: Row<DataTableFeatures, T>;
  /** `row.getVisibleCells()`: the row's cells in display order, the start-pinned ones first. */
  cells: Cell<DataTableFeatures, T, unknown>[];
  /** Each start-pinned column's sticky `left` offset in px, by column id; computed once for every row. */
  pinnedOffsets: Readonly<Record<string, number>>;
  /** Whether the row is ticked; the selection checkbox renders it. */
  selected: boolean;
  /** The caller's handler, bound to the row inside it, so a stable handler leaves the row's props unchanged. */
  onRowClick?: (row: T) => void;
  /**
   * The caller's handler, bound to the row inside it; the browser's own menu is suppressed first. A right-click in a
   * text field inside the row keeps the browser's menu and skips the handler.
   */
  onRowContextMenu?: (row: T, event: React.MouseEvent) => void;
  /** The caller's handler, bound to the row inside it. */
  onRowDoubleClick?: (row: T, event: React.MouseEvent) => void;
  className: string;
  rowHeight: number;
  cursor: string;
  /** Lets the body measure a real row's height (once) and feed it back as the virtualizer estimate. */
  measureRef?: (node: HTMLTableRowElement | null) => void;
  /** Browser-test id stamped on every row as `data-sb` (stripped from production builds). */
  rowTestId?: string;
};

/** Whether two rows' cells lay out the same columns in the same order; TanStack rebuilds columns with their defs. */
function sameColumns<T extends RowData>(
  prev: readonly Cell<DataTableFeatures, T, unknown>[],
  next: readonly Cell<DataTableFeatures, T, unknown>[],
): boolean {
  if (prev === next) return true;
  if (prev.length !== next.length) return false;
  return prev.every((cell, index) => cell.column === next[index].column);
}

/** Whether two sets of pinned offsets pin the same columns at the same offsets. */
function sameOffsets(prev: Readonly<Record<string, number>>, next: Readonly<Record<string, number>>): boolean {
  if (prev === next) return true;
  const prevIds = Object.keys(prev);
  if (prevIds.length !== Object.keys(next).length) return false;
  return prevIds.every((id) => prev[id] === next[id]);
}

/**
 * Whether a body row renders the same for `next` as it did for `prev`, so the row can skip re-rendering.
 *
 * A row renders its data through its columns, laid out by the table's pinning, in its own look. Each is compared by
 * what the row actually draws, not by whether a prop was rebuilt:
 *
 * - its data: `row.id` and `row.original`, so a new data array that keeps the row's object leaves the row alone;
 * - its columns: the visible cells' column instances in order. TanStack rebuilds every column when the column
 *   definitions change (Dispatch rebuilds them on the minute tick, so relative times re-render), and a hide, show,
 *   reorder, or pin changes the order;
 * - where its pinned cells stick: the pinned columns and their offsets, which a pin or a pinned column's resize moves;
 * - whether it is ticked, which the selection checkbox draws;
 * - its class, cursor, height, test id, handlers, and measure ref, as given.
 *
 * A cell that reads anything else (other table state, a value it closes over) must reach the row through one of
 * these: most simply through its column definition, rebuilt when the value changes.
 */
export function rowPropsEqual<T extends RowData>(prev: BodyRowProps<T>, next: BodyRowProps<T>): boolean {
  return (
    prev.row.id === next.row.id &&
    prev.row.original === next.row.original &&
    sameColumns(prev.cells, next.cells) &&
    sameOffsets(prev.pinnedOffsets, next.pinnedOffsets) &&
    prev.selected === next.selected &&
    prev.className === next.className &&
    prev.cursor === next.cursor &&
    prev.rowHeight === next.rowHeight &&
    prev.rowTestId === next.rowTestId &&
    prev.onRowClick === next.onRowClick &&
    prev.onRowContextMenu === next.onRowContextMenu &&
    prev.onRowDoubleClick === next.onRowDoubleClick &&
    prev.measureRef === next.measureRef
  );
}

/** One data row of the body; see {@link rowPropsEqual} for when it re-renders. */
function BodyRow<T extends RowData>({
  row,
  cells,
  pinnedOffsets,
  onRowClick,
  onRowContextMenu,
  onRowDoubleClick,
  className,
  rowHeight,
  cursor,
  measureRef,
  rowTestId,
}: BodyRowProps<T>) {
  probeRow(true);
  return (
    <TableRow
      hover
      ref={measureRef}
      className={className}
      onClick={onRowClick ? () => onRowClick(row.original) : undefined}
      onContextMenu={
        onRowContextMenu
          ? (event) => {
              if (keepsBrowserMenu(event.target)) return;
              event.preventDefault();
              onRowContextMenu(row.original, event);
            }
          : undefined
      }
      onDoubleClick={onRowDoubleClick ? (event) => onRowDoubleClick(row.original, event) : undefined}
      sx={{ cursor, height: rowHeight }}
      data-sb={rowTestId}
      data-row-id={row.id}
    >
      {cells.map((cell) => {
        const meta = cell.column.columnDef.meta;
        const isPinned = cell.column.getIsPinned() === "start";
        return (
          <TableCell
            key={cell.id}
            data-col-id={cell.column.id}
            align={meta?.align ?? "left"}
            className={isPinned ? "frozen-column" : undefined}
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              ...(meta?.compact && { px: 0.25 }),
              ...(isPinned && {
                position: "sticky",
                left: pinnedOffsets[cell.column.id],
                zIndex: 1,
                ...(cell.column.getIsLastColumn("start") && {
                  boxShadow: "2px 0 4px -2px rgba(0, 0, 0, 0.4)",
                }),
              }),
            }}
          >
            {meta?.draggable ? (
              <Box sx={{ ml: `${DRAG_HANDLE_OFFSET}px`, overflow: "hidden", textOverflow: "ellipsis" }}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </Box>
            ) : (
              flexRender(cell.column.columnDef.cell, cell.getContext())
            )}
          </TableCell>
        );
      })}
    </TableRow>
  );
}

/** `rowPropsEqual`, reporting each skip to the row-render probe. */
function rowPropsEqualProbed<T extends RowData>(prev: BodyRowProps<T>, next: BodyRowProps<T>): boolean {
  const same = rowPropsEqual(prev, next);
  if (same) probeRow(false);
  return same;
}

const MemoizedRow = React.memo(BodyRow, rowPropsEqualProbed) as <T extends RowData>(
  props: BodyRowProps<T>,
) => React.JSX.Element;

function VirtualizedBody<T extends RowData>({
  rows,
  pinnedOffsets,
  rowHeight,
  onRowClick,
  onRowContextMenu,
  onRowDoubleClick,
  getRowClassName,
  getRowCursor,
  scrollRef,
  scrollToRowId,
  expandedRowId,
  renderExpandedRow,
  expandedRowHeight,
  rowTestId,
  groups,
  onToggleGroup,
  groupTestId,
  colSpan,
}: {
  rows: Row<DataTableFeatures, T>[];
  pinnedOffsets: Readonly<Record<string, number>>;
  rowHeight: number;
  onRowClick?: (row: T) => void;
  onRowContextMenu?: (row: T, event: React.MouseEvent) => void;
  onRowDoubleClick?: (row: T, event: React.MouseEvent) => void;
  getRowClassName?: (row: T) => string;
  getRowCursor?: (row: T) => string;
  scrollRef: React.RefObject<HTMLDivElement | null>;
  /** When set, scrolls the row with this id into view (e.g. the roving keyboard highlight). */
  scrollToRowId?: string | null;
  expandedRowId?: string | null;
  renderExpandedRow?: (row: T) => React.ReactNode;
  /** The detail row's expected height; the expanded item's size until the rendered detail is measured. */
  expandedRowHeight?: number;
  rowTestId?: string;
  groups?: readonly DataTableGroup[];
  onToggleGroup?: (key: string) => void;
  groupTestId?: string;
  colSpan: number;
}) {
  const items = useMemo(() => interleaveGroups(rows, groups), [rows, groups]);
  // `rowHeight` is only a first-paint guess; the real row is taller (cell padding, borders, the horizontal
  // row-divider). If the estimate stays wrong, the virtualizer's total height disagrees with the real content
  // height and drifts as rows enter/leave the window, nudging the scroll position — a feedback loop that makes the
  // list jitter when it rests between rows (WEB-2139). Rows here are uniform height, so we measure one real row and
  // use its exact (fractional) height as the estimate for every row; the total then matches the rendered content
  // and stays put at any scroll position.
  const [estimatedRowHeight, setEstimatedRowHeight] = useState(rowHeight);
  // Measure exactly once. Correcting the estimate one time is what breaks the feedback loop; re-measuring as rows
  // scroll through the window would force layout reads and re-renders for no benefit, and locking the estimate keeps
  // it from flipping between rows should any row differ by a fraction of a pixel.
  const hasMeasuredRef = useRef(false);
  useLayoutEffect(() => {
    hasMeasuredRef.current = false;
    setEstimatedRowHeight(rowHeight);
  }, [rowHeight]);
  const measureRowHeight = useCallback((node: HTMLTableRowElement | null) => {
    if (!node || hasMeasuredRef.current) return;
    const height = node.getBoundingClientRect().height;
    if (height > 0) {
      hasMeasuredRef.current = true;
      setEstimatedRowHeight((prev) => (Math.abs(height - prev) > 0.5 ? height : prev));
    }
  }, []);
  // Group headers are uniform too, and a hair off the data rows (no cell padding), so they get the same one-time
  // measurement and their own estimate.
  const [groupHeight, setGroupHeight] = useState(rowHeight);
  const hasMeasuredGroupRef = useRef(false);
  useLayoutEffect(() => {
    hasMeasuredGroupRef.current = false;
    setGroupHeight(rowHeight);
  }, [rowHeight]);
  const measureGroupHeight = useCallback((node: HTMLTableRowElement | null) => {
    if (!node || hasMeasuredGroupRef.current) return;
    const height = node.getBoundingClientRect().height;
    if (height > 0) {
      hasMeasuredGroupRef.current = true;
      setGroupHeight((prev) => (Math.abs(height - prev) > 0.5 ? height : prev));
    }
  }, []);
  // The one expanded row is a data row plus its detail. The detail is rendered as a second `<tr>` the virtualizer
  // never sees, so unless its height is folded into that item's size the content is a detail's worth taller than
  // the total the virtualizer scrolls against. At the end of the list that gap is a feedback loop: the detail scrolls
  // out of the rendered window, the content shrinks, the browser clamps the scroll position, the window is recomputed
  // to include the detail again, and the scrollbar bounces. Measured whenever a detail renders and again whenever it
  // resizes, since its height is the caller's and can change while it is shown (a reply that wraps onto another line);
  // `expandedRowHeight` is the estimate until then.
  const [detailHeight, setDetailHeight] = useState(expandedRowHeight ?? 0);
  useLayoutEffect(() => {
    if (expandedRowHeight != null) setDetailHeight(expandedRowHeight);
  }, [expandedRowHeight]);
  // React 18 ignores a ref callback's return value, so the observer lives here and is let go when the node goes.
  const detailObserverRef = useRef<ResizeObserver | null>(null);
  const measureDetailHeight = useCallback((node: HTMLTableRowElement | null) => {
    detailObserverRef.current?.disconnect();
    detailObserverRef.current = null;
    if (!node) return;
    const measure = () => {
      const height = node.getBoundingClientRect().height;
      if (height > 0) setDetailHeight((prev) => (Math.abs(height - prev) > 0.5 ? height : prev));
    };
    measure();
    // jsdom has no ResizeObserver; there the one measurement on mount stands.
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    detailObserverRef.current = observer;
  }, []);
  const expandedIndex = useMemo(
    () =>
      expandedRowId == null || renderExpandedRow == null
        ? -1
        : items.findIndex((item) => item.kind === "row" && item.row.id === expandedRowId),
    [items, expandedRowId, renderExpandedRow],
  );

  const groupIndexes = useMemo(() => items.flatMap((item, index) => (item.kind === "group" ? [index] : [])), [items]);
  const rangeExtractor = useCallback((range: Range) => withActiveGroupHeader(range, groupIndexes), [groupIndexes]);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => {
      if (items[index]?.kind === "group") return groupHeight;
      return index === expandedIndex ? estimatedRowHeight + detailHeight : estimatedRowHeight;
    },
    overscan: 15,
    rangeExtractor,
    // A row scrolled into view at the top lands below the pinned group header instead of hidden behind it.
    scrollPaddingStart: groupIndexes.length > 0 ? groupHeight : 0,
  });

  // Changing `estimateSize`'s value alone doesn't invalidate the virtualizer's cached row measurements, so reset
  // them whenever an estimate changes, or the expansion moves, to force the total height to recompute from the real
  // heights.
  useLayoutEffect(() => {
    virtualizer.measure();
  }, [estimatedRowHeight, groupHeight, detailHeight, expandedIndex, virtualizer]);

  // Keep the externally-driven highlighted row visible: scrolling it into view lets the roving keyboard highlight
  // move through the virtualized list without the row falling outside the rendered window. Only scroll when the id
  // actually changes — depending on `rows` alone would yank the viewport back on every live data refresh even after
  // the user has scrolled away. The scrolled id is recorded only once the row is found, so a not-yet-rendered target
  // is retried on the next data update.
  const lastScrolledRowIdRef = useRef<string | null | undefined>(undefined);
  useLayoutEffect(() => {
    if (scrollToRowId == null) {
      lastScrolledRowIdRef.current = scrollToRowId;
      return;
    }
    if (scrollToRowId === lastScrolledRowIdRef.current) return;
    const index = items.findIndex((item) => item.kind === "row" && item.row.id === scrollToRowId);
    if (index !== -1) {
      virtualizer.scrollToIndex(index, { align: "auto" });
      lastScrolledRowIdRef.current = scrollToRowId;
    }
  }, [scrollToRowId, items, virtualizer]);

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const defaultCursor = onRowClick ? "pointer" : "default";

  return (
    <TableBody>
      {virtualItems.map((virtualItem, position) => {
        const item = items[virtualItem.index];
        // The kept group header can sit apart from the window below it, so each item is spaced from the one before.
        const gap = virtualItem.start - (position === 0 ? 0 : virtualItems[position - 1].end);
        const spacer = gap > 0 ? <tr style={{ height: gap }} /> : null;
        if (item.kind === "group") {
          return (
            <React.Fragment key={`group:${item.group.key}`}>
              {spacer}
              <GroupHeaderRow
                group={item.group}
                colSpan={colSpan}
                rowHeight={rowHeight}
                onToggle={onToggleGroup ? () => onToggleGroup(item.group.key) : undefined}
                testId={groupTestId}
                measureRef={measureGroupHeight}
              />
            </React.Fragment>
          );
        }
        const row = item.row;
        const cells = row.getVisibleCells();
        const className = getRowClassName?.(row.original) ?? "";
        const cursor = getRowCursor ? getRowCursor(row.original) : defaultCursor;
        const expanded = renderExpandedRow != null && expandedRowId != null && row.id === expandedRowId;
        return (
          <React.Fragment key={row.id}>
            {spacer}
            <MemoizedRow
              row={row}
              cells={cells}
              pinnedOffsets={pinnedOffsets}
              selected={row.getIsSelected()}
              rowTestId={rowTestId}
              measureRef={measureRowHeight}
              onRowClick={onRowClick}
              onRowContextMenu={onRowContextMenu}
              onRowDoubleClick={onRowDoubleClick}
              className={className}
              rowHeight={rowHeight}
              cursor={cursor}
            />
            {expanded && (
              <ExpandedDetailRow colSpan={cells.length} measureRef={measureDetailHeight}>
                {renderExpandedRow(row.original)}
              </ExpandedDetailRow>
            )}
          </React.Fragment>
        );
      })}
      {virtualItems.length > 0 && (
        <tr
          style={{
            height: totalSize - (virtualItems[virtualItems.length - 1]?.end ?? 0),
          }}
        />
      )}
    </TableBody>
  );
}

function PlainBody<T extends RowData>({
  rows,
  pinnedOffsets,
  rowHeight,
  onRowClick,
  onRowContextMenu,
  onRowDoubleClick,
  getRowClassName,
  getRowCursor,
  expandedRowId,
  renderExpandedRow,
  rowTestId,
  groups,
  onToggleGroup,
  groupTestId,
  colSpan,
}: {
  rows: Row<DataTableFeatures, T>[];
  pinnedOffsets: Readonly<Record<string, number>>;
  rowHeight: number;
  onRowClick?: (row: T) => void;
  onRowContextMenu?: (row: T, event: React.MouseEvent) => void;
  onRowDoubleClick?: (row: T, event: React.MouseEvent) => void;
  getRowClassName?: (row: T) => string;
  getRowCursor?: (row: T) => string;
  expandedRowId?: string | null;
  renderExpandedRow?: (row: T) => React.ReactNode;
  rowTestId?: string;
  groups?: readonly DataTableGroup[];
  onToggleGroup?: (key: string) => void;
  groupTestId?: string;
  colSpan: number;
}) {
  const defaultCursor = onRowClick ? "pointer" : "default";
  const items = interleaveGroups(rows, groups);

  return (
    <TableBody>
      {items.map((item) => {
        if (item.kind === "group") {
          return (
            <GroupHeaderRow
              key={`group:${item.group.key}`}
              group={item.group}
              colSpan={colSpan}
              rowHeight={rowHeight}
              onToggle={onToggleGroup ? () => onToggleGroup(item.group.key) : undefined}
              testId={groupTestId}
            />
          );
        }
        const row = item.row;
        const cells = row.getVisibleCells();
        const className = getRowClassName?.(row.original) ?? "";
        const cursor = getRowCursor ? getRowCursor(row.original) : defaultCursor;
        const expanded = renderExpandedRow != null && expandedRowId != null && row.id === expandedRowId;
        return (
          <React.Fragment key={row.id}>
            <MemoizedRow
              row={row}
              cells={cells}
              pinnedOffsets={pinnedOffsets}
              selected={row.getIsSelected()}
              rowTestId={rowTestId}
              onRowClick={onRowClick}
              onRowContextMenu={onRowContextMenu}
              onRowDoubleClick={onRowDoubleClick}
              className={className}
              rowHeight={rowHeight}
              cursor={cursor}
            />
            {expanded && (
              <ExpandedDetailRow colSpan={cells.length}>{renderExpandedRow(row.original)}</ExpandedDetailRow>
            )}
          </React.Fragment>
        );
      })}
    </TableBody>
  );
}

function HeaderContextMenu<T extends RowData>({
  column,
  anchorPosition,
  onClose,
  canPin,
  isPinned,
  onTogglePin,
  showHide,
  canHide,
  onHide,
}: {
  column: Column<DataTableFeatures, T> | null;
  anchorPosition: { top: number; left: number } | undefined;
  onClose: () => void;
  canPin: boolean;
  isPinned: boolean;
  onTogglePin: () => void;
  showHide: boolean;
  canHide: boolean;
  onHide: () => void;
}) {
  if (!column) return null;

  const itemSx = { display: "flex", gap: 1, alignItems: "center", fontSize: "0.875rem" } as const;

  return (
    <Menu
      open={anchorPosition != null}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
    >
      {canPin && (
        <MenuItem
          onClick={() => {
            onTogglePin();
            onClose();
          }}
          sx={itemSx}
        >
          {isPinned ? <PushPinOutlinedIcon sx={{ fontSize: 18 }} /> : <PushPinIcon sx={{ fontSize: 18 }} />}
          {isPinned ? "Unpin" : "Pin to left"}
        </MenuItem>
      )}
      {showHide && (
        // Disabled + "Always visible" makes a non-hideable column read as such in the menu.
        <MenuItem
          disabled={!canHide}
          onClick={() => {
            if (!canHide) return;
            onHide();
            onClose();
          }}
          sx={itemSx}
        >
          {canHide ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <LockOutlinedIcon sx={{ fontSize: 18 }} />}
          {canHide ? "Hide column" : "Always visible"}
        </MenuItem>
      )}
    </Menu>
  );
}

/**
 * Native TanStack resize affordance that doubles as the hover/active column divider.
 *
 * The always-visible low-contrast separator is the column's `border-right` (see `DriverDataGrid`); this handle sits on
 * top of that edge and brightens its own border on hover or while resizing, giving the higher-contrast divider cue.
 *
 * Double-clicking the handle auto-fits the column: it collapses to the narrowest width that still shows the header and
 * the widest rendered cell without clipping (see `columnAutoFit`).
 */
function ColumnResizeHandle<T extends RowData>({ header }: { header: Header<DataTableFeatures, T, unknown> }) {
  const isResizing = header.column.getIsResizing();

  const handleAutoFit = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const tableEl = e.currentTarget.closest("table");
      if (!tableEl) return;
      const measured = measureColumnContentWidth(tableEl, header.column.id);
      if (measured == null) return;
      // Auto-fit fits content, so it floors at the header-fit `initialMinWidth` (never the icon-only resize floor): a
      // double-click should keep the header readable, while a manual drag can still collapse the column to its icons.
      const meta = header.column.columnDef.meta;
      const width = clampAutoFitWidth(measured, {
        minSize: meta?.initialMinWidth ?? header.column.columnDef.minSize,
        maxSize: header.column.columnDef.maxSize,
      });
      header.getContext().table.setColumnSizing((prev) => ({ ...prev, [header.column.id]: width }));
    },
    [header],
  );

  return (
    <Box
      data-sb={TestIds.driverView.columnResizeHandle}
      onMouseDown={header.getResizeHandler()}
      onTouchStart={header.getResizeHandler()}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={handleAutoFit}
      title="Drag to resize · double-click to fit"
      sx={{
        position: "absolute",
        right: 0,
        top: 0,
        height: "100%",
        width: "8px",
        cursor: "col-resize",
        userSelect: "none",
        touchAction: "none",
        borderRight: "2px solid",
        borderColor: isResizing ? "rgb(228, 228, 231)" : "transparent",
        "&:hover": { borderColor: "rgb(228, 228, 231)" },
      }}
    />
  );
}

function HeaderCellInner<T extends RowData>({
  header,
  resizable,
  showGrabber,
  dragListeners,
  dragAttributes,
}: {
  header: Header<DataTableFeatures, T, unknown>;
  resizable: boolean;
  showGrabber: boolean;
  dragListeners?: DraggableSyntheticListeners;
  dragAttributes?: DraggableAttributes;
}) {
  const canResize = resizable && header.column.getCanResize();
  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {showGrabber && (
          <Box
            component="span"
            data-sb={TestIds.driverView.columnHeaderGrabber}
            {...dragAttributes}
            {...(dragListeners ?? {})}
            onClick={(e) => e.stopPropagation()}
            sx={{
              cursor: "grab",
              display: "inline-flex",
              color: "rgb(161, 161, 170)",
              touchAction: "none",
              "&:hover": { color: "white" },
              "&:active": { cursor: "grabbing" },
            }}
          >
            <DragIndicatorIcon sx={{ fontSize: 16 }} />
          </Box>
        )}
        {header.isPlaceholder ? null : header.column.getCanSort() ? (
          <TableSortLabel
            active={header.column.getIsSorted() !== false}
            direction={header.column.getIsSorted() === "desc" ? "desc" : "asc"}
            onClick={header.column.getToggleSortingHandler()}
            sx={{ color: "inherit", "& .MuiTableSortLabel-icon": { color: "inherit" } }}
          >
            {flexRender(header.column.columnDef.header, header.getContext())}
          </TableSortLabel>
        ) : (
          flexRender(header.column.columnDef.header, header.getContext())
        )}
      </Box>
      {canResize && <ColumnResizeHandle header={header} />}
    </>
  );
}

type HeaderCellShared<T extends RowData> = {
  header: Header<DataTableFeatures, T, unknown>;
  headerHeight: number;
  columnSizing: ColumnSizingState;
  leftPinnedOffset: number | null;
  resizable: boolean;
  onHeaderContextMenu: (e: React.MouseEvent, column: Column<DataTableFeatures, T>) => void;
  onHeaderClick?: (column: Column<DataTableFeatures, T>, event: React.MouseEvent<HTMLElement>) => void;
};

function baseHeaderCellSx<T extends RowData>(
  header: Header<DataTableFeatures, T, unknown>,
  columnSizing: ColumnSizingState,
  leftPinnedOffset: number | null,
): SxProps<Theme> {
  const meta = header.column.columnDef.meta;
  const isPinned = leftPinnedOffset != null;
  const isResized = header.column.id in columnSizing;
  const resizedWidth = isResized ? fixedColumnPixelWidth(header.column) : null;
  return {
    position: "sticky",
    top: 0,
    zIndex: isPinned ? 3 : 2,
    fontWeight: 600,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    transition: "background-color 150ms ease",
    ...(resizedWidth != null ? { width: `${resizedWidth}px` } : {}),
    ...(meta?.compact && { px: 0.25 }),
    ...(isPinned && {
      left: leftPinnedOffset,
      ...(header.column.getIsLastColumn("start") && { boxShadow: "2px 0 4px -2px rgba(0, 0, 0, 0.4)" }),
    }),
  };
}

function StaticHeaderCell<T extends RowData>({
  header,
  headerHeight,
  columnSizing,
  leftPinnedOffset,
  resizable,
  onHeaderContextMenu,
  onHeaderClick,
}: HeaderCellShared<T>) {
  const meta = header.column.columnDef.meta;
  const isPinned = leftPinnedOffset != null;
  return (
    <TableCell
      data-col-id={header.column.id}
      align={meta?.align ?? "left"}
      className={isPinned ? "frozen-column" : undefined}
      onContextMenu={(e) => onHeaderContextMenu(e, header.column)}
      onClick={onHeaderClick ? (e) => onHeaderClick(header.column, e) : undefined}
      sx={{
        height: headerHeight,
        ...(onHeaderClick ? { cursor: "pointer" } : {}),
        ...baseHeaderCellSx(header, columnSizing, leftPinnedOffset),
      }}
    >
      <HeaderCellInner header={header} resizable={resizable} showGrabber={false} />
    </TableCell>
  );
}

function SortableHeaderCell<T extends RowData>({
  header,
  headerHeight,
  columnSizing,
  leftPinnedOffset,
  resizable,
  onHeaderContextMenu,
  onHeaderClick,
}: HeaderCellShared<T>) {
  const meta = header.column.columnDef.meta;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver } = useSortable({
    id: header.column.id,
  });

  return (
    <TableCell
      ref={(node: HTMLTableCellElement | null) => setNodeRef(node)}
      data-col-id={header.column.id}
      align={meta?.align ?? "left"}
      onContextMenu={(e) => onHeaderContextMenu(e, header.column)}
      onClick={onHeaderClick ? (e) => onHeaderClick(header.column, e) : undefined}
      sx={{
        ...baseHeaderCellSx(header, columnSizing, leftPinnedOffset),
        ...(onHeaderClick ? { cursor: "pointer" } : {}),
        transform: DndCss.Transform.toString(transform ? { ...transform, scaleX: 1, scaleY: 1 } : null),
        transition,
        height: headerHeight,
        opacity: isDragging ? 0.4 : 1,
        zIndex: isDragging ? 4 : 2,
        ...(isOver && !isDragging ? { bgcolor: "rgb(82, 82, 91)" } : {}),
      }}
    >
      <HeaderCellInner
        header={header}
        resizable={resizable}
        showGrabber
        dragListeners={listeners}
        dragAttributes={attributes}
      />
    </TableCell>
  );
}

function ColumnHeaderRow<T extends RowData>({
  table,
  headerHeight,
  columnSizing,
  hidingEnabled,
  dndEnabled,
  onHideColumn,
  onColumnPinningChange,
  onHeaderClick,
}: {
  table: DataTableInstance<T>;
  headerHeight: number;
  columnSizing: ColumnSizingState;
  hidingEnabled: boolean;
  dndEnabled: boolean;
  onHideColumn?: (columnId: string) => void;
  onColumnPinningChange?: (left: string[]) => void;
  onHeaderClick?: (column: Column<DataTableFeatures, T>, event: React.MouseEvent<HTMLElement>) => void;
}) {
  const [contextMenuPosition, setContextMenuPosition] = useState<{ top: number; left: number } | undefined>();
  const [contextMenuColumn, setContextMenuColumn] = useState<Column<DataTableFeatures, T> | null>(null);
  const resizable = table.options.enableColumnResizing === true;

  const alwaysPinnedIds = new Set(
    table
      .getAllLeafColumns()
      .filter((c) => c.columnDef.meta?.alwaysPinned)
      .map((c) => c.id),
  );

  const handleHeaderContextMenu = (e: React.MouseEvent, column: Column<DataTableFeatures, T>) => {
    const isAlwaysPinned = alwaysPinnedIds.has(column.id);
    const canPin = onColumnPinningChange != null && !isAlwaysPinned;
    const showHide = hidingEnabled && !isAlwaysPinned;
    if (!canPin && !showHide) return;
    e.preventDefault();
    setContextMenuPosition({ top: e.clientY - 6, left: e.clientX + 2 });
    setContextMenuColumn(column);
  };

  const togglePin = (column: Column<DataTableFeatures, T>) => {
    if (!onColumnPinningChange) return;
    const manageablePinned = table
      .getStartLeafColumns()
      .filter((c) => !alwaysPinnedIds.has(c.id))
      .map((c) => c.id);
    const pinned = column.getIsPinned() === "start";
    onColumnPinningChange(
      pinned ? manageablePinned.filter((id) => id !== column.id) : [...manageablePinned, column.id],
    );
  };

  const handleContextMenuClose = useCallback(() => {
    setContextMenuPosition(undefined);
    setContextMenuColumn(null);
  }, []);

  const sortableIds = table.getCenterVisibleLeafColumns().map((c) => c.id);

  return (
    <TableHead>
      {table.getHeaderGroups().map((headerGroup) => {
        const pinnedOffsets = leftPinnedOffsets(headerGroup.headers.map((header) => header.column));

        const renderHeaderCell = (header: Header<DataTableFeatures, T, unknown>) => {
          const meta = header.column.columnDef.meta;
          const isPinned = header.column.getIsPinned() === "start";
          const leftPinnedOffset = isPinned ? (pinnedOffsets[header.column.id] ?? 0) : null;
          const shared: HeaderCellShared<T> = {
            header,
            headerHeight,
            columnSizing,
            leftPinnedOffset,
            resizable,
            onHeaderContextMenu: handleHeaderContextMenu,
            onHeaderClick,
          };
          const isDraggable = dndEnabled && !isPinned && meta?.draggable === true;
          return isDraggable ? (
            <SortableHeaderCell key={header.id} {...shared} />
          ) : (
            <StaticHeaderCell key={header.id} {...shared} />
          );
        };

        return (
          <TableRow key={headerGroup.id} sx={{ height: headerHeight }}>
            {dndEnabled ? (
              <SortableContext items={sortableIds} strategy={horizontalListSortingStrategy}>
                {headerGroup.headers.map(renderHeaderCell)}
              </SortableContext>
            ) : (
              headerGroup.headers.map(renderHeaderCell)
            )}
          </TableRow>
        );
      })}
      <HeaderContextMenu
        column={contextMenuColumn}
        anchorPosition={contextMenuPosition}
        onClose={handleContextMenuClose}
        canPin={
          contextMenuColumn != null && onColumnPinningChange != null && !alwaysPinnedIds.has(contextMenuColumn.id)
        }
        isPinned={contextMenuColumn?.getIsPinned() === "start"}
        onTogglePin={() => {
          if (contextMenuColumn) togglePin(contextMenuColumn);
        }}
        showHide={hidingEnabled}
        canHide={contextMenuColumn?.getCanHide() ?? false}
        onHide={() => {
          if (!contextMenuColumn) return;
          onHideColumn?.(contextMenuColumn.id);
          contextMenuColumn.toggleVisibility(false);
        }}
      />
    </TableHead>
  );
}

export default function DataTable<T extends RowData>({
  table,
  onRowClick,
  onRowContextMenu,
  onRowDoubleClick,
  onHeaderClick,
  getRowClassName,
  getRowCursor,
  noRowsContent = "No Results",
  pagination = true,
  pageSizeOptions = [20, 60, 100],
  toolbar,
  rowHeight = 64,
  headerHeight = 70,
  virtualize = false,
  enableColumnHiding = false,
  onHideColumn,
  contentFit = false,
  columnPinning,
  onColumnPinningChange,
  scrollToRowId,
  expandedRowId,
  renderExpandedRow,
  expandedRowHeight,
  rowTestId,
  containerTestId,
  groups,
  onToggleGroup,
  groupTestId,
  sx,
}: DataTableProps<T>) {
  const rows = table.getRowModel().rows;
  const hasGroups = groups != null && groups.length > 0;
  const scrollRef = useRef<HTMLDivElement>(null);
  const columnSizing = table.state.columnSizing;
  const leafColumns = table.getAllLeafColumns();
  const leafHeaders = table.getHeaderGroups().at(-1)?.headers ?? [];
  // Every row's pinned cells stick at the same offsets, so they are summed once here rather than in each row.
  const pinnedOffsets = leftPinnedOffsets(leafHeaders.map((header) => header.column));

  // Apply TanStack's native column-pinning state. Pinning positions columns independently of `columnOrder` (which then
  // governs only the unpinned/center columns). The effective left set is the user override (`columnPinning` prop) when
  // present, else the columns' `meta.frozen` default; resolved leaf-column ids handle accessorKey columns without an
  // explicit id.
  const metaFrozenLeft = leafColumns.filter((c) => c.columnDef.meta?.frozen).map((c) => c.id);
  const alwaysPinnedLeft = leafColumns.filter((c) => c.columnDef.meta?.alwaysPinned).map((c) => c.id);
  const effectiveLeftKey = resolveLeftPinned(
    metaFrozenLeft,
    alwaysPinnedLeft,
    leafColumns.map((c) => c.id),
    columnPinning,
  ).join("|");
  useLayoutEffect(() => {
    const left = effectiveLeftKey ? effectiveLeftKey.split("|") : [];
    const current = table.state.columnPinning.start;
    if (current.length !== left.length || current.some((id, i) => id !== left[i])) {
      table.setColumnPinning({ start: left, end: [] });
    }
  }, [table, effectiveLeftKey]);

  const hasDraggableColumns = leafColumns.some((c) => c.columnDef.meta?.draggable);

  // The container publishes its visible width for a detail row's content (`ExpandedDetailRow`) and the column header's
  // height for group headers to stick beneath (`GroupHeaderRow`). The drag context wraps the container, so toggling it
  // remounts it.
  const hasExpandedRows = renderExpandedRow != null;
  usePublishedCssVar(
    scrollRef,
    hasExpandedRows,
    VISIBLE_WIDTH_VAR,
    (container) => container,
    (container) => container.clientWidth,
    hasDraggableColumns,
  );
  usePublishedCssVar(
    scrollRef,
    hasGroups,
    HEADER_HEIGHT_VAR,
    (container) => container.querySelector("thead"),
    (head) => head.getBoundingClientRect().height,
    hasDraggableColumns,
  );

  const sensors = useSensors(
    // Distance constraint so a plain click on the header (sort/filter) doesn't start a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );

  const handleColumnDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const centerIds = table.getCenterVisibleLeafColumns().map((c) => c.id);
      const from = centerIds.indexOf(String(active.id));
      const to = centerIds.indexOf(String(over.id));
      if (from === -1 || to === -1) return;
      table.setColumnOrder(arrayMove(centerIds, from, to));
    },
    [table],
  );

  const minTableWidth =
    TABLE_MIN_WIDTH_BUFFER_PX +
    leafHeaders.reduce((sum, header) => {
      const col = header.column;
      // Resized/pinned columns hold an exact pixel width; unresized columns floor at their header-fit initial width so
      // the table keeps headers visible on first load, while a user-shrunk column lets the table grow narrower too.
      if (col.id in columnSizing || col.getIsPinned() === "start") return sum + fixedColumnPixelWidth(col);
      const meta = col.columnDef.meta;
      return sum + (meta?.initialMinWidth ?? col.columnDef.minSize ?? col.getSize());
    }, 0);

  const tableContent = (
    <TableContainer
      ref={scrollRef}
      data-sb={containerTestId}
      sx={{ flex: 1, overflow: "auto", scrollbarGutter: "stable", ...(contentFit ? { minHeight: 0 } : {}) }}
    >
      <Table
        stickyHeader
        sx={{
          tableLayout: "fixed",
          borderCollapse: "separate",
          borderSpacing: 0,
          minWidth: minTableWidth,
        }}
      >
        <colgroup>
          {leafHeaders.map((header) => {
            const col = header.column;
            const meta = col.columnDef.meta;
            const flex = meta?.flex;
            const isResized = col.id in columnSizing;
            const isPinned = col.getIsPinned() === "start";
            const minW = col.columnDef.minSize;
            const maxW = col.columnDef.maxSize;

            // Resized and pinned columns are locked to an exact pixel width. Pinning a flex column must drop its flex
            // so its rendered width equals the value the sticky left-offset accumulator sums; otherwise it renders
            // wider than assumed and overlaps the next pinned column, hiding the divider when scrolled.
            if (isResized || isPinned) {
              const w = fixedColumnPixelWidth(col);
              return <col key={col.id} style={{ width: `${w}px`, minWidth: `${w}px`, maxWidth: `${w}px` }} />;
            }

            const width = flex != null && flex > 0 ? undefined : col.getSize();
            // Unresized columns floor at their header-fit `initialMinWidth` so headers stay visible on first load; the
            // smaller `minSize` only takes over once the user drags the column narrower (see `fixedColumnPixelWidth`).
            const initialMinW = meta?.initialMinWidth ?? minW;
            return (
              <col
                key={col.id}
                style={{
                  width: width ? `${width}px` : undefined,
                  minWidth: initialMinW ? `${initialMinW}px` : undefined,
                  maxWidth: maxW ? `${maxW}px` : undefined,
                }}
              />
            );
          })}
        </colgroup>
        <ColumnHeaderRow
          table={table}
          headerHeight={headerHeight}
          columnSizing={columnSizing}
          hidingEnabled={enableColumnHiding}
          dndEnabled={hasDraggableColumns}
          onHideColumn={onHideColumn}
          onColumnPinningChange={onColumnPinningChange}
          onHeaderClick={onHeaderClick}
        />
        {rows.length === 0 && !hasGroups ? (
          <TableBody>
            <TableRow>
              <TableCell colSpan={leafHeaders.length} sx={{ textAlign: "center", py: 4 }}>
                {noRowsContent}
              </TableCell>
            </TableRow>
          </TableBody>
        ) : virtualize ? (
          <VirtualizedBody
            rows={rows}
            pinnedOffsets={pinnedOffsets}
            rowHeight={rowHeight}
            onRowClick={onRowClick}
            onRowContextMenu={onRowContextMenu}
            onRowDoubleClick={onRowDoubleClick}
            getRowClassName={getRowClassName}
            getRowCursor={getRowCursor}
            scrollRef={scrollRef}
            scrollToRowId={scrollToRowId}
            expandedRowId={expandedRowId}
            renderExpandedRow={renderExpandedRow}
            expandedRowHeight={expandedRowHeight}
            rowTestId={rowTestId}
            groups={groups}
            onToggleGroup={onToggleGroup}
            groupTestId={groupTestId}
            colSpan={table.getVisibleLeafColumns().length}
          />
        ) : (
          <PlainBody
            rows={rows}
            pinnedOffsets={pinnedOffsets}
            rowHeight={rowHeight}
            onRowClick={onRowClick}
            onRowContextMenu={onRowContextMenu}
            onRowDoubleClick={onRowDoubleClick}
            getRowClassName={getRowClassName}
            getRowCursor={getRowCursor}
            expandedRowId={expandedRowId}
            renderExpandedRow={renderExpandedRow}
            rowTestId={rowTestId}
            groups={groups}
            onToggleGroup={onToggleGroup}
            groupTestId={groupTestId}
            colSpan={table.getVisibleLeafColumns().length}
          />
        )}
      </Table>
    </TableContainer>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        ...(contentFit ? { maxHeight: "100%" } : { height: "100%" }),
        ...sx,
      }}
    >
      {toolbar != null ? contentFit ? <Box sx={{ flexShrink: 0 }}>{toolbar}</Box> : toolbar : null}
      {hasDraggableColumns ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToHorizontalAxis]}
          onDragEnd={handleColumnDragEnd}
        >
          {tableContent}
        </DndContext>
      ) : (
        tableContent
      )}
      {pagination && rows.length > 0 && (
        <Box sx={contentFit ? { flexShrink: 0 } : undefined}>
          <TablePagination
            component="div"
            count={table.getFilteredRowModel().rows.length}
            page={table.state.pagination.pageIndex}
            rowsPerPage={table.state.pagination.pageSize}
            onPageChange={(_e, page) => table.setPageIndex(page)}
            onRowsPerPageChange={(e) => table.setPageSize(Number(e.target.value))}
            rowsPerPageOptions={pageSizeOptions}
          />
        </Box>
      )}
    </Box>
  );
}
